import { useState, useEffect } from "react";
import { Activity, Clock, LogIn, LogOut, CheckCircle, Truck, FileText, Calendar, RefreshCw, Filter, User, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../utils/api";

const API = `/api`;

// Map action types to icons and colors
const ACTION_CONFIG = {
  clock_in: { icon: LogIn, color: "text-[#22c55e]", bg: "bg-[#22c55e]/10", label: "Clocked In" },
  clock_out: { icon: LogOut, color: "text-[#f59e0b]", bg: "bg-[#f59e0b]/10", label: "Clocked Out" },
  created: { icon: FileText, color: "text-[#3b82f6]", bg: "bg-[#3b82f6]/10", label: "Created" },
  updated: { icon: RefreshCw, color: "text-purple-400", bg: "bg-purple-500/10", label: "Updated" },
  deleted: { icon: FileText, color: "text-red-400", bg: "bg-red-500/10", label: "Deleted" },
  completed: { icon: CheckCircle, color: "text-[#22c55e]", bg: "bg-[#22c55e]/10", label: "Completed" },
  stage_completed: { icon: CheckCircle, color: "text-[#22c55e]", bg: "bg-[#22c55e]/10", label: "Stage Done" },
  stage_skipped: { icon: RefreshCw, color: "text-zinc-400", bg: "bg-zinc-500/10", label: "Stage Skipped" },
  login_success: { icon: LogIn, color: "text-[#22c55e]", bg: "bg-[#22c55e]/10", label: "Login" },
  login_failed: { icon: LogIn, color: "text-red-400", bg: "bg-red-500/10", label: "Failed Login" },
  status_change: { icon: RefreshCw, color: "text-[#3b82f6]", bg: "bg-[#3b82f6]/10", label: "Status Change" },
};

const ENTITY_CONFIG = {
  job: { icon: Truck, label: "Job" },
  quote: { icon: FileText, label: "Quote" },
  appointment: { icon: Calendar, label: "Appointment" },
  timer: { icon: Clock, label: "Timer" },
  user: { icon: User, label: "User" },
};

function ActivityLogSection() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?entity_type=${filter}` : "";
      const res = await api.get(`${API}/admin/activity-log${params}`);
      setActivities(res.data.activities || []);
    } catch (err) {
      console.error("Failed to fetch activity log:", err);
      toast.error("Failed to load activity log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [filter]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString("en-AU", { 
      day: "numeric", 
      month: "short", 
      hour: "2-digit", 
      minute: "2-digit",
      hour12: true
    });
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return `${diffDays}d ago`;
  };

  const getActionDetails = (activity) => {
    const action = activity.action || "updated";
    const config = ACTION_CONFIG[action] || ACTION_CONFIG.updated;
    return config;
  };

  const getEntityDetails = (activity) => {
    const entity = activity.entity_type || "job";
    return ENTITY_CONFIG[entity] || ENTITY_CONFIG.job;
  };

  const formatActivityMessage = (activity) => {
    const action = activity.action;
    const details = activity.details || {};
    const user = activity.user || details.user || "Unknown";

    // Clock in/out messages
    if (action === "clock_in") {
      return `${user} clocked in`;
    }
    if (action === "clock_out") {
      const duration = details.duration_minutes;
      const notes = details.notes;
      let msg = `${user} clocked out`;
      if (duration !== undefined) {
        const hours = Math.floor(duration / 60);
        const mins = Math.round(duration % 60);
        if (hours > 0) {
          msg += ` (${hours}h ${mins}m total)`;
        } else {
          msg += ` (${mins} mins total)`;
        }
      }
      if (notes) msg += ` - "${notes}"`;
      return msg;
    }

    // Login messages
    if (action === "login_success") {
      return `${details.username || user} logged in from ${details.ip || "unknown"}`;
    }
    if (action === "login_failed") {
      return `Failed login attempt for ${details.username || "unknown"} from ${details.ip || "unknown"}`;
    }

    // Status change
    if (action === "status_change" || details.status) {
      return `Status changed to "${details.status || details.new_status}"`;
    }

    // Stage completion/skip
    if (action === "stage_completed") {
      const stageName = details.stage_name || 'Unknown';
      const vehicle = details.vehicle ? ` on ${details.vehicle}` : '';
      return `${user} completed "${stageName}"${vehicle}`;
    }
    if (action === "stage_skipped") {
      const stageName = details.stage_name || 'Unknown';
      const vehicle = details.vehicle ? ` on ${details.vehicle}` : '';
      return `${user} skipped "${stageName}"${vehicle}`;
    }

    // Generic messages
    if (action === "created") return `New ${activity.entity_type || 'item'} created`;
    if (action === "deleted") return `${activity.entity_type || 'item'} deleted`;
    if (action === "completed") return `${activity.entity_type || 'item'} marked complete`;
    if (action === "updated" && details.tags) return `Tags updated: ${details.tags.join(", ")}`;

    return `${action} ${activity.entity_type || 'item'}`;
  };

  const filters = [
    { key: "all", label: "All Activity" },
    { key: "job", label: "Jobs" },
    { key: "quote", label: "Quotes" },
    { key: "appointment", label: "Appointments" },
  ];

  return (
    <div className="space-y-4" data-testid="activity-log-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/10 flex items-center justify-center">
            <Activity size={20} className="text-purple-400" />
          </div>
          <div>
            <h3 className="font-heading text-lg uppercase tracking-wide text-white">Activity Log</h3>
            <p className="text-zinc-500 font-body text-sm">Track all system activity and user actions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider bg-[#09090b] border border-white/10 text-zinc-400 hover:text-white transition-colors cursor-pointer"
              data-testid="activity-filter-btn"
            >
              <Filter size={14} />
              {filters.find(f => f.key === filter)?.label || "All"}
              <ChevronDown size={14} />
            </button>
            {showFilters && (
              <div className="absolute right-0 top-full mt-1 bg-[#0a0a0a] border border-white/10 z-10 min-w-[150px]">
                {filters.map(f => (
                  <button
                    key={f.key}
                    onClick={() => { setFilter(f.key); setShowFilters(false); }}
                    className={`w-full px-3 py-2 text-xs uppercase tracking-wider text-left hover:bg-white/5 transition-colors cursor-pointer border-none ${
                      filter === f.key ? "text-[#3b82f6] bg-[#3b82f6]/10" : "text-zinc-400"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Refresh Button */}
          <button
            onClick={fetchActivities}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider bg-[#09090b] border border-white/10 text-zinc-400 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
            data-testid="activity-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Activity List */}
      <div className="bg-[#09090b] border border-white/5">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw size={24} className="animate-spin text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500 font-body text-sm">Loading activity...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="p-12 text-center">
            <Activity size={32} className="text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 font-body">No activity recorded yet</p>
            <p className="text-zinc-600 font-body text-sm mt-1">Activity will appear here as you use the system</p>
          </div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto">
            {activities.map((activity, idx) => {
              const actionConfig = getActionDetails(activity);
              const entityConfig = getEntityDetails(activity);
              const ActionIcon = actionConfig.icon;
              const EntityIcon = entityConfig.icon;

              return (
                <div
                  key={activity.id || idx}
                  className="p-4 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors"
                  data-testid={`activity-item-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`w-9 h-9 ${actionConfig.bg} flex items-center justify-center flex-shrink-0`}>
                      <ActionIcon size={16} className={actionConfig.color} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs uppercase tracking-wider font-body ${actionConfig.color}`}>
                          {actionConfig.label}
                        </span>
                        <span className="text-zinc-700">•</span>
                        <span className="flex items-center gap-1 text-xs text-zinc-500">
                          <EntityIcon size={12} />
                          {entityConfig.label}
                        </span>
                      </div>
                      <p className="text-sm text-white font-body">
                        {formatActivityMessage(activity)}
                      </p>
                      {activity.entity_id && (
                        <p className="text-xs text-zinc-600 font-mono mt-1">
                          Job ID: {activity.entity_id.substring(0, 8)}...
                        </p>
                      )}
                    </div>

                    {/* Timestamp */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-zinc-400 font-mono">
                        {formatRelativeTime(activity.timestamp)}
                      </p>
                      <p className="text-xs text-zinc-600 font-mono mt-0.5">
                        {formatTimestamp(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {activities.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-[#09090b] border border-white/5 p-4 text-center">
            <p className="font-heading text-2xl text-white">{activities.length}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Events</p>
          </div>
          <div className="bg-[#09090b] border border-white/5 p-4 text-center">
            <p className="font-heading text-2xl text-[#22c55e]">
              {activities.filter(a => a.action === "clock_in").length}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Clock Ins</p>
          </div>
          <div className="bg-[#09090b] border border-white/5 p-4 text-center">
            <p className="font-heading text-2xl text-[#f59e0b]">
              {activities.filter(a => a.action === "clock_out").length}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Clock Outs</p>
          </div>
          <div className="bg-[#09090b] border border-white/5 p-4 text-center">
            <p className="font-heading text-2xl text-[#3b82f6]">
              {activities.filter(a => a.action === "login_success").length}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Logins</p>
          </div>
        </div>
      )}
    </div>
  );
}

export { ActivityLogSection };
